import { ActionContext, createStore } from 'vuex';
// models
import { Actions, BazaarState, Mutations } from './models/store.model';
import {
  Auth,
  defaultAuth,
  LoginData,
  RegisterData,
} from './models/auth.model';
import { Message, sortMessages } from './models/message.model';
import { Schedule } from './models/schedule.model';
import { Content } from './models/content.model';
import { Sponsor } from './models/sponsor.model';
import { FAQ } from './models/faq.model';
import { User } from './models/user.model';
// services
import authService from './services/auth.service';
import messageService from './services/message.service';
import scheduleService from './services/schedule.service';
import contentService from './services/content.service';
import faqService from './services/faq.service';
import userService from './services/user.service';
import sponsorService from './services/sponsor.service';
import rosterService from './services/roster.service';
// modules
import { drawerStore } from './core/drawer.store';
import { Child, Person, Roster, Slot } from '@/models/roster.model';
import { addDays, format } from 'date-fns';
import { getCookieValue } from './models/util';
import { Wiki, WikiPage } from './models/wiki.model';
import wikiService from './services/wiki.service';
import { File as FileItem } from './models/file.model';

type Context = ActionContext<BazaarState, BazaarState>;

export default createStore<BazaarState>({
  state: {
    auth: defaultAuth,
    messages: [],
    scheduleItem: null,
    schedule: [],
    messageItem: null,
    contents: [],
    contentItem: null,
    sponsors: [],
    sponsorItem: null,
    faqs: [],
    faqItem: null,
    rosters: [],
    roster: null,
    people: [],
    person: null,
    children: [],
    users: [],
    wiki: null,
    editWiki: false,
    wikiPage: null,
    wikiPages: [],
    wikiTemplates: [],
    files: [],
  },
  modules: {
    ds: drawerStore,
  },
  mutations: {
    [Mutations.SetAuth](state: BazaarState, auth: Auth) {
      state.auth = auth;
    },
    [Mutations.SetMessages](state: BazaarState, messages: Message[]) {
      state.messages = messages;
    },
    [Mutations.SetMessageItem](state: BazaarState, message: Message) {
      state.messageItem = message;
    },
    [Mutations.SetSchedules](state: BazaarState, schedules: Schedule[]) {
      state.schedule = schedules;
    },
    [Mutations.SetScheduleItem](state: BazaarState, schedule: Schedule) {
      state.scheduleItem = schedule;
    },
    [Mutations.SetContents](state: BazaarState, contents: Content[]) {
      state.contents = contents;
    },
    [Mutations.SetContentItem](state: BazaarState, content: Content) {
      state.contentItem = content;
    },
    [Mutations.SetSponsors](state: BazaarState, sponsors: Sponsor[]) {
      state.sponsors = sponsors;
    },
    [Mutations.SetSponsorItem](state: BazaarState, sponsor: Sponsor) {
      state.sponsorItem = sponsor;
    },
    [Mutations.SetFAQs](state: BazaarState, faqs: FAQ[]) {
      state.faqs = faqs;
    },
    [Mutations.SetFAQItem](state: BazaarState, faq: FAQ) {
      state.faqItem = faq;
    },
    [Mutations.SetRosters](state: BazaarState, rosters: Roster[]) {
      state.rosters = rosters;
    },
    [Mutations.SetEditWiki](state: BazaarState, edit: boolean) {
      state.editWiki = edit;
    },
    [Mutations.SetWiki](state: BazaarState, wiki: Wiki | null) {
      state.wiki = wiki;
      state.wikiPage = wiki?.page || null;
    },
    [Mutations.SetWikiPage](state: BazaarState, wikiPage: WikiPage) {
      state.wikiPage = wikiPage;
    },
    [Mutations.SetWikiPages](state: BazaarState, wikiPages: WikiPage[]) {
      state.wikiPages = wikiPages;
    },
    [Mutations.SetWikiTemplates](
      state: BazaarState,
      wikiTemplates: WikiPage[],
    ) {
      state.wikiTemplates = wikiTemplates;
    },
    [Mutations.SetRoster](state: BazaarState, roster: Roster) {
      state.roster = roster;
    },
    [Mutations.SetPeople](state: BazaarState, people: Person[]) {
      state.people = people;
    },
    [Mutations.SetPerson](state: BazaarState, person: Person) {
      state.person = person;

      //Thu, 18 Dec 2013 12:00:00 UTC
      document.cookie = `id=${person.id}; expires=${format(
        addDays(new Date(), 100),
        'EEE, d MMM yyyy HH:mm:ss',
      )} UTC`;
    },
    [Mutations.SetAsPerson](state: BazaarState, person: Person) {
      state.person = person;
    },
    [Mutations.UnsetPerson](state: BazaarState, person: Person) {
      state.person = null;

      //Thu, 18 Dec 2013 12:00:00 UTC
      document.cookie = `id=${person.id}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    },
    [Mutations.SetChildren](state: BazaarState, children: Child[]) {
      state.children = children;
    },
    [Mutations.SetUsers](state: BazaarState, users: User[]) {
      state.users = users;
    },
    [Mutations.SetFiles](state: BazaarState, files: FileItem[]) {
      state.files = files;
    },
  },
  actions: {
    async [Actions.GetAuth]({ commit }: Context): Promise<Auth | null> {
      const response = await authService.get();
      commit(
        Mutations.SetAuth,
        response.data && response.data.expires ? response.data : defaultAuth,
      );

      return response.data ? response.data : defaultAuth;
    },
    async [Actions.Login](
      { commit }: Context,
      login: LoginData,
    ): Promise<boolean> {
      const response = await authService.login(login);

      commit(Mutations.SetAuth, response.data);

      return response.status;
    },
    async [Actions.Register](
      { commit }: Context,
      registration: RegisterData,
    ): Promise<boolean> {
      const response = await authService.register(registration);

      commit(Mutations.SetAuth, response.data);

      return response.status;
    },
    async [Actions.Logout]({ commit }: Context) {
      await authService.logout();

      commit(Mutations.SetAuth, defaultAuth);
    },
    async [Actions.GetMessageItem]({ commit }: Context, id: number) {
      const response = await messageService.get(id);
      commit(Mutations.SetMessageItem, response.data);
    },
    async [Actions.GetMessages]({ commit }: Context) {
      const response = await messageService.getList();
      commit(Mutations.SetMessages, response.data);
    },
    async [Actions.AddMessage]({ dispatch }: Context, message: Message) {
      await messageService.post(message);
      dispatch(Actions.GetMessages);
    },
    async [Actions.UpdateMessage]({ dispatch }: Context, message: Message) {
      await messageService.put(message);
      dispatch(Actions.GetMessages);
    },
    async [Actions.GetScheduleItem]({ commit }: Context, id: number) {
      const response = await scheduleService.get(id);
      commit(Mutations.SetScheduleItem, response.data);
    },
    async [Actions.GetSchedules]({ commit }: Context) {
      const response = await scheduleService.getList();
      commit(Mutations.SetSchedules, response.data);
    },
    async [Actions.AddSchedule]({ dispatch }: Context, schedule: Schedule) {
      await scheduleService.post(schedule);
      dispatch(Actions.GetSchedules);
    },
    async [Actions.UpdateSchedule]({ dispatch }: Context, schedule: Schedule) {
      await scheduleService.put(schedule);
      dispatch(Actions.GetSchedules);
    },
    async [Actions.GetContentItem]({ commit }: Context, id: number) {
      const response = await contentService.get(id);
      commit(Mutations.SetContentItem, response.data);
    },
    async [Actions.GetContents]({ commit }: Context) {
      const response = await contentService.getList();
      commit(Mutations.SetContents, response.data);
    },
    async [Actions.AddContent]({ dispatch }: Context, content: Content) {
      await contentService.post(content);
      dispatch(Actions.GetContents);
    },
    async [Actions.UpdateContent]({ dispatch }: Context, content: FormData) {
      await contentService.put(content);
      dispatch(Actions.GetContents);
    },
    async [Actions.GetSponsorItem]({ commit }: Context, id: number) {
      const response = await sponsorService.get(id);
      commit(Mutations.SetSponsorItem, response.data);
    },
    async [Actions.GetSponsors]({ commit }: Context) {
      const response = await sponsorService.getList();
      commit(Mutations.SetSponsors, response.data);
    },
    async [Actions.AddSponsor]({ dispatch }: Context, sponsor: Sponsor) {
      await sponsorService.post(sponsor);
      dispatch(Actions.GetSponsors);
    },
    async [Actions.UpdateSponsor]({ dispatch }: Context, sponsor: Sponsor) {
      await sponsorService.put(sponsor);
      dispatch(Actions.GetSponsors);
    },
    async [Actions.GetFAQItem]({ commit }: Context, id: number) {
      const response = await faqService.get(id);
      commit(Mutations.SetFAQItem, response.data);
    },
    async [Actions.GetFAQs]({ commit }: Context) {
      const response = await faqService.getList();
      commit(Mutations.SetFAQs, response.data);
    },
    async [Actions.AddFAQ]({ dispatch }: Context, faq: FAQ) {
      await faqService.post(faq);
      dispatch(Actions.GetFAQs);
    },
    async [Actions.UpdateFAQ]({ dispatch }: Context, faq: FAQ) {
      await faqService.put(faq);
      dispatch(Actions.GetFAQs);
    },
    async [Actions.GetWiki]({ commit }: Context, slug: string) {
      const response = await wikiService.getBySlug(slug);
      commit(Mutations.SetWiki, response.data);
    },
    async [Actions.GetWikiById]({ commit }: Context, id: number) {
      const response = await wikiService.get(id);
      commit(Mutations.SetWiki, response.data);
    },
    async [Actions.GetWikiPages]({ commit }: Context) {
      const response = await wikiService.getList();
      commit(
        Mutations.SetWikiPages,
        response.data.filter((p) => !p.is_template),
      );
      commit(
        Mutations.SetWikiTemplates,
        response.data.filter((p) => p.is_template),
      );
    },
    async [Actions.AddWikiPage]({ commit }: Context, page: WikiPage) {
      const wikiPage = await wikiService.post(page);
      commit(Mutations.SetWikiPage, wikiPage);
    },
    async [Actions.UpdateWikiPage]({ commit }: Context, page: WikiPage) {
      const wikiPage = await wikiService.put(page);
      commit(Mutations.SetWikiPage, wikiPage);
    },
    async [Actions.GetFiles]({ state, commit }: Context, file: File) {
      const res = await wikiService.getFiles();
      commit(Mutations.SetFiles, res.data);
    },
    async [Actions.AddFile]({ state, commit }: Context, file: File) {
      const res = await wikiService.putFile(file);
      commit(Mutations.SetFiles, [...state.files, res.data]);
    },
    async [Actions.GetRosterItem]({ commit }: Context, id: number) {
      const response = await faqService.get(id);
      commit(Mutations.SetRoster, response.data);
    },
    async [Actions.GetRosters]({ commit }: Context) {
      const response = await rosterService.getList();
      commit(Mutations.SetRosters, response.data);
    },
    async [Actions.AddRoster]({ dispatch }: Context, roster: Roster) {
      await rosterService.post(roster);
      dispatch(Actions.GetRosters);
    },
    async [Actions.UpdateRoster]({ dispatch }: Context, roster: Roster) {
      await rosterService.put(roster);
      dispatch(Actions.GetRosters);
    },
    async [Actions.AddPersonToSlot](
      { dispatch }: Context,
      {
        roster,
        slot,
        index,
        person,
      }: { roster: Roster; slot: Slot; index: number; person: Person },
    ) {
      await rosterService.updateRosterSignup(roster, slot, index, person);
      dispatch(Actions.GetRosters);
    },
    async [Actions.GetPeople]({ commit }: Context) {
      const res = await rosterService.getPeopleList();

      const people = res.data;

      let id = getCookieValue('id');

      if (id) {
        const person = people.find((p) => p.id === parseInt(id));

        if (person) {
          commit(Mutations.SetPerson, person);
        }
      }

      commit(Mutations.SetPeople, people);
    },
    async [Actions.AddPerson]({ dispatch }: Context, person: Person) {
      const res = await rosterService.postPerson(person);

      const p = res.data;

      dispatch(Actions.GetPeople);
      return p;
    },
    async [Actions.UpdatePerson]({}: Context, person: Person) {
      const res = await rosterService.putPerson(person);

      return res.data;
    },
    async [Actions.GetChildren]({ commit }: Context) {
      const res = await rosterService.getChildrenList();

      const children = res.data;

      commit(Mutations.SetChildren, children);
    },
    async [Actions.AddChild]({ dispatch }: Context, person: Child) {
      const res = await rosterService.postChild(person);

      const p = res.data;

      dispatch(Actions.GetPeople);
      return p;
    },
    async [Actions.UpdateChild]({}: Context, person: Child) {
      const res = await rosterService.putChild(person);

      return res.data;
    },
    async [Actions.GetUsers]({ commit }: Context) {
      const response = await userService.getList();
      commit(Mutations.SetUsers, response.data);
    },
    async [Actions.UpdateUser]({ dispatch }: Context, user: User) {
      await userService.put(user);

      dispatch(Actions.GetUsers);

      return user;
    },
  },
  getters: {
    getContent: (state) => (slug: string) =>
      state.contents.find((c) => c.slug === slug),
    latestMessages: (state) =>
      [...state.messages.sort(sortMessages)].slice(0, 3),
    faqTop: (state) => [...state.faqs.sort((a, b) => a.id - b.id)].slice(0, 2),
  },
});
